<template>
  <div id="rendered-service-list">
    <vs-alert color="success" v-if="services.length === 0">
      {{ $t('services.no_services_msg') }}
    </vs-alert>
    <div v-else>
      <div class="flex flex-wrap-reverse items-center flex-grow justify-between mb-base">
        <!-- ITEMS PER PAGE -->
        <vs-dropdown vs-trigger-click class="cursor-pointer">
          <div
            class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
          >
            <span class="mr-2">
              {{ (currentPage * pageSize - (pageSize - 1)) | persian_digits }} {{ $t('forms.to') }}
              {{
                serviceDataCount - currentPage * pageSize > 0
                  ? currentPage * pageSize
                  : serviceDataCount | persian_digits
              }}
              {{ $t('forms.from') }} {{ serviceDataCount | persian_digits }}
            </span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
          </div>
          <vs-dropdown-menu>
            <vs-dropdown-item @click="pageSize = 5">
              <span>{{ 5 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 10">
              <span>{{ 10 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 50">
              <span>{{ 50 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 100">
              <span>{{ 100 | persian_digits }}</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
        </div>
      </div>

      <vs-table ref="table" :data="serviceData" @selected="handleSelected" class="card-table">
        <template slot="thead">
          <vs-th>{{ $t('places.category') }}</vs-th>
          <vs-th>{{ $t('invoices.receiver') }}</vs-th>
          <vs-th>{{ $t('entity.state') }}</vs-th>
          <vs-th>{{ $t('services.requested_services') }}</vs-th>
          <vs-th>{{ $t('invoices.created_at') }}</vs-th>
          <vs-th>{{ $t('forms.actions') }}</vs-th>
        </template>

        <template slot-scope="{ data }">
          <tbody id="table-body" v-if="data.length">
            <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
              <vs-td>
                <h4>{{ $t(`places.categories.${tr.category}`) }}</h4>
                <vs-row class="mt-3">{{ $t(`places.subjects.${tr.subject}`) }}</vs-row>
              </vs-td>
              <vs-td>
                <p class="mr-2">
                  {{ $t(`users.genders.name.${tr.gender}`) }} {{ tr.firstName }}
                  {{ tr.lastName }}
                </p>
                <small>
                  {{ tr.age | age_range }}
                </small>
              </vs-td>
              <vs-td>
                <cell-badges :data="tr.status ? tr.status : 'pending'" local="services.status." />
              </vs-td>
              <vs-td>
                <div>
                  {{ tr.items[0].count | persian_digits }} {{ $t(`units.${tr.items[0].unit}`) }}
                  {{ $t(`health_services.${tr.subject}.${tr.items[0].service}`) }}
                  <template v-if="tr.items.length > 1">{{
                    $t('invoices.items_more', { count: tr.items.length - 1 }) | persian_digits
                  }}</template>
                </div>
              </vs-td>
              <vs-td>
                {{ tr.created_at | datetime }}
              </vs-td>
              <vs-td>
                <div v-if="tr.status" class="flex flex-col gap-1">
                  <vs-button
                    type="border"
                    size="small"
                    class="mr-2"
                    @click.stop="$router.push({ name: 'invoice-view', params: { id: tr.invoiceId } })"
                    v-if="!['expired'].includes(tr.status)"
                  >
                    {{ $t('invoices.view_invoice') }}
                  </vs-button>
                  <vs-button
                    type="border"
                    size="small"
                    icon-pack="feather"
                    icon="icon-x"
                    color="danger"
                    @click.stop="openCancelConfirm(tr)"
                    v-if="['pending', 'accepted'].includes(tr.status)"
                  >
                    {{ $t('invoices.cancel') }}
                  </vs-button>
                </div>
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import CellBadges from '@/views/pages/system/businesses/business-list/CellBadges'

export default {
  name: 'CaseManagerServiceList',
  components: { CellBadges },
  data() {
    return {
      services: [],
      pageSize: 10,
      skip: 0
    }
  },
  computed: {
    serviceDataCount() {
      return this.services.length
    },
    serviceData() {
      return this.services.slice(this.skip, this.skip + this.pageSize)
    },
    totalPages() {
      return Math.ceil(this.services.length / this.pageSize)
    },
    currentPage: {
      get() {
        return Math.ceil(this.skip / this.pageSize + 1)
      },
      set(val) {
        this.skip = (val - 1) * this.pageSize
      }
    }
  },
  methods: {
    openCancelConfirm(request) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('invoices.cancel'),
        text: this.$t('messages.are_you_sure'),
        accept: () => {
          this.$store
            .dispatch('request/updateRequestBySubject', {
              category: request.category,
              subject: request.subject,
              id: request.id,
              activity: 'UserCancellation',
              data: {}
            })
            .then(() => {
              this.services[this.services.findIndex(s => s.id === request.id)].status = 'canceled'
              this.$notifySuccess(this.$t('services.successfully_canceled'))
            })
        },
        acceptText: this.$t('button.yes'),
        cancelText: this.$t('button.no')
      })
    },
    handleSelected(s) {
      this.$router.push({ name: 'rendered-service-view', params: { id: s.id } })
    }
  },
  created() {
    this.$vs.loading()

    this.$store
      .dispatch('request/fetchHealthRequests', {
        query: { caseManagerUserID: this.$store.state.AppActiveUser.id, sort: { created_at: -1 } },
        skip: 0,
        limit: 500
      })
      .then(res => {
        const data = JSON.parse(JSON.stringify(res.data))
        if (data.length === 0) {
          this.$vs.loading.close()
          this.services = []
        }
        let services = []
        let count = 0
        data.forEach(request => {
          this.$store
            .dispatch('request/fetchRequestDetailsBySubject', {
              id: request.id,
              subject: request.subject,
              category: request.category
            })
            .then(res => {
              services.push({ ...request, details: res.data })
              count += 1
              if (count === data.length) {
                this.services = services.sort((a, b) =>
                  this.$moment(a.created_at).isSameOrBefore(b.created_at) ? 1 : -1
                )
                this.$vs.loading.close()
              }
            })
            .catch(() => this.$vs.loading.close())
        })
      })
      .catch(() => this.$vs.loading.close())
  }
}
</script>

<style>
.card-map-top {
  overflow: hidden;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}
</style>
